import { createSlice } from "@reduxjs/toolkit";

/* ------------------------------------------------------------------
 🧩 PV STRING — Flat structure
-------------------------------------------------------------------*/
export const createEmptyString = (id) => ({
  id: `String ${id}`,
  quantity: "",
  protective_type: "",
  wiring_type: "",
  wiring_phase_csa: "",
  wiring_earth_csa: "",
  test_voc: "",
  test_isc: "",
  test_irradiance: "",
  polarity_check: "",
  test_voltage: "",
  pos_earth: "",
  neg_earth: "",
  earth_continuity: "",
  isolator_dc_switch: "",
  isolator_location: "",
  isolator_functional_check: "",
});

/* ------------------------------------------------------------------
 🧩 MPPT
-------------------------------------------------------------------*/
const createMPPT = (name) => ({
  name,
  strings: [],
});

/* ------------------------------------------------------------------
 🧩 INVERTER
-------------------------------------------------------------------*/
const createInverter = (name, noOfMppt = 2, noOfStrings = 4) => ({
  name,
  serialNo: "",
  noOfMppt,
  noOfStrings,
  mppts: Array.from({ length: noOfMppt }, (_, i) =>
    createMPPT(`MPPT ${i + 1}`)
  ),
});

/* ------------------------------------------------------------------
 🧩 INITIAL STATE
-------------------------------------------------------------------*/
const initialState = {
  selectedPanel: null, // now just a string
  invertersList: [],
};

/* ------------------------------------------------------------------
 🧩 SLICE
-------------------------------------------------------------------*/
const pva_TestReportSlice = createSlice({
  name: "pva_test",
  initialState,
  reducers: {
    selectPanel: (state, action) => {
      state.selectedPanel = action.payload?.name || null; // only name
    },

    addInverter: (state, action) => {
      const { name, noOfMppt = 2, noOfStrings = 4 } = action.payload || {};
      if (!name) return;
      state.invertersList.push(createInverter(name, noOfMppt, noOfStrings));
    },

    removeInverter: (state, action) => {
      const index = action.payload;
      state.invertersList.splice(index, 1);
    },

    updateInverterSerial: (state, action) => {
      const { inverterIndex, serialNo } = action.payload;
      const inverter = state.invertersList[inverterIndex];
      if (inverter) inverter.serialNo = serialNo;
    },

    addStringToMPPT: (state, action) => {
      const { inverterIndex, mpptIndex, stringId } = action.payload;
      const inverter = state.invertersList[inverterIndex];
      const mppt = inverter?.mppts?.[mpptIndex];
      if (!mppt) return;

      const num = stringId?.replace("String ", "") || mppt.strings.length + 1;
      const newString = createEmptyString(num);
      newString.id = stringId || `String ${num}`;
      mppt.strings.push(newString);
    },

    removeStringFromMPPT: (state, action) => {
      const { inverterIndex, mpptIndex, stringIndex } = action.payload;
      state.invertersList?.[inverterIndex]?.mppts?.[mpptIndex]?.strings?.splice(
        stringIndex,
        1
      );
    },

    updateStringField: (state, action) => {
      const { inverterIndex, mpptIndex, stringIndex, field, value } =
        action.payload;

      const str =
        state.invertersList?.[inverterIndex]?.mppts?.[mpptIndex]?.strings?.[
          stringIndex
        ];
      if (str && Object.prototype.hasOwnProperty.call(str, field)) {
        str[field] = value;
      }
    },

    setPVAInitial: (state, action) => {
      const { invertersList, selectedPanel } = action.payload || {};
      if (Array.isArray(invertersList)) state.invertersList = invertersList;
      if (selectedPanel !== undefined)
        state.selectedPanel = selectedPanel || null;
    },

    resetStringArray: (state) => {
      state.selectedPanel = null;
      state.invertersList = [];
    },
  },
});

/* ------------------------------------------------------------------
 EXPORT ACTIONS + REDUCER
-------------------------------------------------------------------*/
export const {
  selectPanel,
  addInverter,
  selectInverter,
  removeInverter,
  updateInverterSerial,
  updateStringField,
  addStringToMPPT,
  removeStringFromMPPT,
  setPVAInitial,
  resetStringArray,
} = pva_TestReportSlice.actions;

export default pva_TestReportSlice.reducer;
